<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      loading: true,
      curMonth: new Date().getMonth() + 1,
      curYear: new Date().getFullYear(),
      rawRecords: [],
      holidays: [],
      statusMeta: {
        present:  { bg: '#22c55e', text: '#ffffff', label: 'Present' },   // green
        absent:   { bg: '#ef4444', text: '#ffffff', label: 'Absent' },    // red
        half_day: { bg: '#eab308', text: '#ffffff', label: 'Half Day' },  // yellow/gold
        late:     { bg: '#f97316', text: '#ffffff', label: 'Late' },      // orange
        on_leave: { bg: '#8b5cf6', text: '#ffffff', label: 'Leave' },     // purple
        holiday:  { bg: '#14b8a6', text: '#ffffff', label: 'Holiday' },   // teal
        week_off: { bg: '#cbd5e1', text: '#475569', label: 'Week Off' },  // gray
      },
    };
  },
  computed: {
    daysInMonth() {
      return new Date(this.curYear, this.curMonth, 0).getDate();
    },
    monthLabel() {
      return new Date(this.curYear, this.curMonth - 1, 1)
        .toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    },
    // weekday-letter header only — no dates here, dates live inside each employee's own row
    weekdayHeaders() {
      const days = [];
      for (let d = 1; d <= this.daysInMonth; d++) {
        const dt = new Date(this.curYear, this.curMonth - 1, d);
        days.push(dt.toLocaleDateString('en-IN', { weekday: 'short' })[0]);
      }
      return days;
    },
    dayGridStyle() {
      return { gridTemplateColumns: `repeat(${this.daysInMonth}, minmax(30px, 1fr))` };
    },
    // date (YYYY-MM-DD) -> holiday name, filtered to the month/year currently in view
    holidaysByDate() {
      const map = {};
      for (const h of this.holidays) {
        const d = new Date(h.date);
        if (d.getFullYear() === this.curYear && d.getMonth() + 1 === this.curMonth) {
          map[d.getDate()] = h.name;
        }
      }
      return map;
    },
    employeeRows() {
      const map = {};
      for (const r of this.rawRecords) {
        const empId = r.employee?.id || r.employee_id;
        if (!empId) continue;
        if (!map[empId]) {
          map[empId] = {
            id: empId,
            name: r.employee?.full_name || '—',
            code: r.employee?.code || '',
            statusByDate: {},
          };
        }
        const d = new Date(r.date).getDate();
        map[empId].statusByDate[d] = r.status;
      }
      return Object.values(map)
        .map((emp) => ({ ...emp, cells: this.buildCells(emp.statusByDate) }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
    this.fetchHolidays();
    this.fetchData();
  },
  methods: {
    async fetchHolidays() {
      try {
        const res = await axios.get('/hrms/holidays', { params: { search: '', per_page: 200 } });
        this.holidays = res.data.data.data || [];
      } catch (error) {
        console.error('Holiday fetch failed:', error.message);
        this.holidays = [];
      }
    },
async fetchData() {
  this.loading = true;
  try {
    let page = 1;
    let lastPage = 1;
    let all = [];
    do {
      const res = await axios.get('/hrms/attendances', {
        params: { month: this.curMonth, year: this.curYear, per_page: 50, page },
      });
      const payload = res.data.data;
      all = all.concat(payload.data || []);
      lastPage = payload.last_page || 1;
      page++;
    } while (page <= lastPage);
    this.rawRecords = all;
  } catch (error) {
    this.$swalToast?.(error.message, 'error');
    this.rawRecords = [];
  } finally {
    this.loading = false;
  }
},
    prevMonth() {
      this.curMonth--;
      if (this.curMonth < 1) { this.curMonth = 12; this.curYear--; }
      this.fetchData();
    },
    nextMonth() {
      this.curMonth++;
      if (this.curMonth > 12) { this.curMonth = 1; this.curYear++; }
      this.fetchData();
    },
    resolveStatus(statusByDate, d) {
      const real = statusByDate[d];
      if (real) return real;              
      if (this.holidaysByDate[d]) return 'holiday';
      const dow = new Date(this.curYear, this.curMonth - 1, d).getDay();
      if (dow === 0) return 'week_off';
      return null;
    },
    buildCells(statusByDate) {
      const cells = [];
      for (let d = 1; d <= this.daysInMonth; d++) {
        const status = this.resolveStatus(statusByDate, d);
        const prevStatus = d > 1 ? this.resolveStatus(statusByDate, d - 1) : null;
        const nextStatus = d < this.daysInMonth ? this.resolveStatus(statusByDate, d + 1) : null;
        cells.push({
          date: d,
          status,
          roundLeft: status && prevStatus !== status,
          roundRight: status && nextStatus !== status,
          isSingle: status && prevStatus !== status && nextStatus !== status,
        });
      }
      return cells;
    },
    cellStyle(cell) {
      if (!cell.status || cell.isSingle) return {};
      const c = this.statusMeta[cell.status] || this.statusMeta.present;
      return {
        background: c.bg,
        color: c.text,
        borderTopLeftRadius: cell.roundLeft ? '14px' : '0',
        borderBottomLeftRadius: cell.roundLeft ? '14px' : '0',
        borderTopRightRadius: cell.roundRight ? '14px' : '0',
        borderBottomRightRadius: cell.roundRight ? '14px' : '0',
      };
    },
    circleStyle(cell) {
      const c = this.statusMeta[cell.status] || this.statusMeta.present;
      return { background: c.bg, color: c.text };
    },
    statusLabel(key) {
      return this.statusMeta[key]?.label || key;
    },
    cellTitle(cell) {
      if (!cell.status) return '';
      if (cell.status === 'holiday' && this.holidaysByDate[cell.date]) return this.holidaysByDate[cell.date];
      return this.statusLabel(cell.status);
    },
    initials(name) {
      if (!name) return '?';
      const p = name.trim().split(/\s+/);
      return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
    },
    avatarColor(name) {
      const palette = [
        ['#eef2ff', '#4f46e5'], ['#ecfeff', '#0891b2'], ['#f0fdf4', '#16a34a'],
        ['#fff7ed', '#ea580c'], ['#fdf4ff', '#a21caf'], ['#fef2f2', '#dc2626'],
      ];
      let h = 0;
      for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
      return palette[Math.abs(h) % palette.length];
    },
  },
};
</script>

<template>
  <div class="tac-card">
    <div class="tac-header">
      <div class="d-flex align-items-center gap-2">
        <button class="tac-nav-btn" @click="prevMonth"><i class="ti ti-chevron-left"></i></button>
        <span class="tac-month">{{ monthLabel }}</span>
        <button class="tac-nav-btn" @click="nextMonth"><i class="ti ti-chevron-right"></i></button>
      </div>
      <div class="tac-legend">
        <span v-for="(m, key) in statusMeta" :key="key" class="tac-legend-item">
          <span class="tac-legend-dot" :style="{ background: m.bg }"></span>{{ m.label }}
        </span>
      </div>
    </div>

    <div class="tac-scroll">
      <div class="tac-grid-wrap">
        <!-- weekday-letter header row only (no date numbers here) -->
        <div class="tac-row tac-row-head">
          <div class="tac-emp-col tac-emp-col-head">Employee</div>
          <div class="tac-days-track" :style="dayGridStyle">
            <div v-for="(w, idx) in weekdayHeaders" :key="idx" class="tac-day-head">{{ w }}</div>
          </div>
        </div>

        <div v-if="loading" class="tac-loading">Loading…</div>
        <div v-else-if="!employeeRows.length" class="tac-empty">No attendance records for this month.</div>

        <div v-else v-for="emp in employeeRows" :key="emp.id" class="tac-row">
          <div class="tac-emp-col">
            <span class="tac-avatar" :style="{ background: avatarColor(emp.name)[0], color: avatarColor(emp.name)[1] }">
              {{ initials(emp.name) }}
            </span>
            <div class="d-flex flex-column">
              <span class="tac-emp-name">{{ emp.name }}</span>
              <span v-if="emp.code" class="tac-emp-code">{{ emp.code }}</span>
            </div>
          </div>
          <div class="tac-days-track" :style="dayGridStyle">
            <div
              v-for="cell in emp.cells"
              :key="cell.date"
              class="tac-cell"
              :class="{ 'tac-cell--plain': !cell.status }"
              :style="cellStyle(cell)"
              :title="cellTitle(cell)"
            >
              <span v-if="cell.isSingle" class="tac-circle" :style="circleStyle(cell)">{{ cell.date }}</span>
              <template v-else>{{ cell.date }}</template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tac-card { background: #fff; border: 1px solid #eef2f7; border-radius: 16px; padding: 18px 20px; box-shadow: 0 1px 3px rgba(0,0,0,.03); }

.tac-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.tac-nav-btn { width: 30px; height: 30px; border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #475569; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.tac-nav-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
.tac-month { font-size: 15px; font-weight: 700; color: #0f172a; min-width: 140px; text-align: center; }

.tac-legend { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.tac-legend-item { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: #64748b; }
.tac-legend-dot { width: 8px; height: 8px; border-radius: 50%; }

.tac-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.tac-grid-wrap { min-width: max-content; }

.tac-row { display: flex; align-items: center; border-bottom: 1px solid #f1f5f9; }
.tac-row:last-child { border-bottom: none; }
.tac-row-head { border-bottom: 2px solid #eef2f7; }

.tac-emp-col {
  position: sticky; left: 0; z-index: 2; background: #fff;
  display: flex; align-items: center; gap: 8px;
  width: 190px; min-width: 190px; padding: 10px 12px 10px 0;
  border-right: 1px solid #f1f5f9;
}
.tac-emp-col-head { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; }

.tac-avatar { width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; }
.tac-emp-name { font-size: 12.5px; font-weight: 600; color: #1e293b; line-height: 1.2; }
.tac-emp-code { font-size: 9.5px; font-weight: 700; color: #94a3b8; letter-spacing: .3px; }

.tac-days-track { display: grid; gap: 0; padding: 6px 0; flex: 1; }

.tac-day-head {
  text-align: center; font-size: 10.5px; font-weight: 700; color: #cbd5e1;
  text-transform: uppercase; padding: 4px 0;
}

.tac-cell {
  height: 28px; display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #334155; cursor: default;
  transition: transform .1s ease;
}
.tac-cell--plain { color: #94a3b8; font-weight: 500; }
.tac-cell:not(.tac-cell--plain) { font-weight: 700; }
.tac-cell:not(.tac-cell--plain):hover { transform: scale(1.08); }

.tac-circle {
  width: 24px; height: 24px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
}

.tac-loading, .tac-empty { padding: 30px 12px; text-align: center; font-size: 12.5px; color: #94a3b8; }

@media (max-width: 576px) {
  .tac-emp-col { width: 130px; min-width: 130px; }
  .tac-emp-code { display: none; }
}
</style>